import type { IAlbumRequest, IAlbumResponse, IAlbumResponseV1 } from "@/types"
import { AlbumSettings, Location, ZipSummary } from "@/models"

export enum AlbumPrivacyOptions {
  Public = "public",
  Protected = "protected",
  Private = "private",
}

export class Album {
  public id?: number
  public eventVaultId?: number
  public title?: string
  public description?: string
  public date?: Date
  public closeDate?: Date
  public qrUrl?: string
  public shareLink?: string
  public coverImage?: File | null
  public coverImageUrl?: string | null
  public status?: AlbumStatus = AlbumStatus.Unknown
  public passcode?: string
  public isLocked: boolean = false
  public isHidden: boolean = false
  public totalPhotos?: number
  public totalVideos?: number
  public displayOrder?: number
  public zipSummary?: ZipSummary
  public location?: Location
  public settings: AlbumSettings = new AlbumSettings()

  constructor(album: Partial<Album>) {
    this.id = album.id
    this.eventVaultId = album.eventVaultId
    this.title = album.title
    this.description = album.description
    this.date = album.date
    this.closeDate = album.closeDate
    this.shareLink = album.shareLink
    this.qrUrl = album.qrUrl
    this.coverImage = album.coverImage
    this.coverImageUrl = album.coverImageUrl
    this.status = album.status
    this.passcode = album.passcode
    this.isLocked = album.isLocked ?? false
    this.isHidden = album.isHidden ?? false
    this.totalPhotos = album.totalPhotos
    this.totalVideos = album.totalVideos
    this.displayOrder = album.displayOrder
    this.zipSummary = album.zipSummary
    this.location = album.location
    this.settings = album.settings ?? this.settings
  }

  public getFormattedPrivacy() {
    if (this.isLocked)
      return AlbumPrivacyOptions.Protected
    if (this.isHidden)
      return AlbumPrivacyOptions.Private
    return AlbumPrivacyOptions.Public
  }

  public static toRequest(album: Album): IAlbumRequest {
    const request: IAlbumRequest = {
      Title: "",
    }

    if (album.title)
      request.Title = album.title
    if (album.description)
      request.Description = album.description
    if (album.date)
      request.AlbumDate = album.date
    if (album.coverImage)
      request.CoverImageFile = album.coverImage
    if (album.passcode)
      request.AlbumPasscode = album.passcode
    if ("isLocked" in album)
      request.IsLocked = album.isLocked
    if ("isHidden" in album)
      request.IsHidden = album.isHidden

    return request
  }

  public static fromResponse(response?: IAlbumResponse): Album {
    const zipSummary = response?.ZipJobSummary ? ZipSummary.fromResponse(response.ZipJobSummary) : undefined
    const location = response?.Location ? Location.fromResponse(response.Location) : undefined
    const albumId = response?.EventAlbumID
    const eventId = response?.EventVaultID

    const shareLink = (eventId && albumId && response.AccessToken)
      ? `/guest/access/${eventId}/${response?.AccessToken}?album=${albumId}`
      : undefined

    return new Album({
      id: albumId,
      eventVaultId: eventId,
      title: response?.Title,
      description: response?.Description ?? undefined,
      date: response?.AlbumDate ? new Date(response?.AlbumDate) : undefined,
      closeDate: response?.AlbumCloseDate ? new Date(response?.AlbumCloseDate) : undefined,
      coverImageUrl: response?.CoverImageURL ?? undefined,
      qrUrl: response?.FullPathToQRCode,
      shareLink,
      passcode: response?.AlbumPasscode,
      isLocked: response?.IsLocked ?? false,
      isHidden: response?.IsHidden ?? false,
      totalPhotos: response?.TotalPhotos,
      totalVideos: response?.TotalVideos,
      settings: AlbumSettings.fromResponse(response?.AlbumSettings),
      status: AlbumStatus.fromResponse(response?.AlbumStatus),
      zipSummary,
      location,
    })
  }

  public static fromResponseV1(response?: IAlbumResponseV1): Album {
    const albumId = response?.eventAlbumID
    const eventId = response?.eventVaultID

    const zipSummary = response?.zipJobSummary ? ZipSummary.fromResponseV1(response.zipJobSummary) : undefined
    const location = response?.location ? Location.fromResponseV1(response.location) : undefined

    const shareLink = (eventId && albumId && response.accessToken)
      ? `/guest/access/${eventId}/${response?.accessToken}?album=${albumId}`
      : undefined

    return new Album({
      id: albumId,
      eventVaultId: eventId,
      title: response?.title,
      description: response?.description ?? undefined,
      date: response?.albumDate ? new Date(response?.albumDate) : undefined,
      closeDate: response?.albumCloseDate ? new Date(response?.albumCloseDate) : undefined,
      coverImageUrl: response?.coverImageURL ?? undefined,
      shareLink,
      passcode: response?.albumPasscode,
      isLocked: response?.isLocked ?? false,
      isHidden: response?.isHidden ?? false,
      totalPhotos: response?.totalPhotos,
      totalVideos: response?.totalVideos,
      displayOrder: response?.displayOrder,
      status: AlbumStatus.fromResponse(response?.albumStatus),
      qrUrl: response?.fullPathToQRCode,
      location,
      settings: AlbumSettings.fromResponseV1(response?.albumSettings),
      zipSummary,
    })
  }
}

export class AlbumStatus {
  public static Draft = new AlbumStatus("draft")
  public static Ready = new AlbumStatus("ready")
  public static Live = new AlbumStatus("live")
  public static Closed = new AlbumStatus("closed")
  public static Expired = new AlbumStatus("expired")
  public static Archived = new AlbumStatus("archived")
  public static Deleted = new AlbumStatus("deleted")
  public static Unknown = new AlbumStatus("unknown")

  public name: string

  constructor(name: string) {
    this.name = name
  }

  public static fromResponse(status?: number) {
    switch (status) {
      case 0:
        return AlbumStatus.Draft
      case 1:
        return AlbumStatus.Ready
      case 2:
        return AlbumStatus.Live
      case 3:
        return AlbumStatus.Closed
      case 4:
        return AlbumStatus.Expired
      case 5:
        return AlbumStatus.Archived
      case 6:
        return AlbumStatus.Deleted
      default:
        return AlbumStatus.Unknown
    }
  }
}
